import type { PurchaseOrderInput } from './useProcurement';

/**
 * Cálculo e montagem do pedido de compra, fora do componente.
 *
 * Existe separado porque o defeito que motivou esta tela era **de contrato**:
 * o formulário enviava `supplierId`/`unitPrice` e o backend lê
 * `vendorId`/`unitValue`, então criar pedido pela interface nunca funcionou.
 * Um erro assim não aparece no typecheck (os dois lados eram `string`/`number`)
 * nem no verificador de forma da resposta (a forma do envelope é a mesma —
 * o que diverge são os campos). Isolado aqui, ele fica coberto por teste.
 */

export interface PurchaseOrderLine {
  productId: string;
  sku: string;
  description: string;
  search: string;
  quantity: string;
  unitValue: string;
  /** Percentual digitado pelo operador, como na tela de referência. */
  icmsPercent: string;
  /** Percentual digitado pelo operador, como na tela de referência. */
  ipiPercent: string;
}

export const emptyLine = (): PurchaseOrderLine => ({
  productId: '',
  sku: '',
  description: '',
  search: '',
  quantity: '',
  unitValue: '',
  icmsPercent: '',
  ipiPercent: '',
});

/** Aceita vírgula decimal; vazio e lixo viram 0. */
export function num(value: string): number {
  const parsed = Number.parseFloat(value.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Base do item, sem imposto. */
export function lineBase(line: PurchaseOrderLine): number {
  return +(num(line.quantity) * num(line.unitValue)).toFixed(2);
}

/**
 * Imposto do item em reais.
 *
 * A tela recebe percentual (como a referência) e o domínio guarda valor:
 * `PurchaseOrderItem.taxValues` é `Record<string, number>` em reais e o próprio
 * agregado soma esses valores no total do item.
 */
export function lineTax(line: PurchaseOrderLine): number {
  return +(lineBase(line) * ((num(line.icmsPercent) + num(line.ipiPercent)) / 100)).toFixed(2);
}

export function lineTotal(line: PurchaseOrderLine): number {
  return +(lineBase(line) + lineTax(line)).toFixed(2);
}

export interface PurchaseOrderTotals {
  subtotal: number;
  taxTotal: number;
  total: number;
}

export function totals(lines: PurchaseOrderLine[]): PurchaseOrderTotals {
  const subtotal = +lines.reduce((sum, l) => sum + lineBase(l), 0).toFixed(2);
  const taxTotal = +lines.reduce((sum, l) => sum + lineTax(l), 0).toFixed(2);
  return { subtotal, taxTotal, total: +(subtotal + taxTotal).toFixed(2) };
}

/** Linha que ainda não tem produto escolhido ou quantidade não vai para o servidor. */
export function isComplete(line: PurchaseOrderLine): boolean {
  return !!line.productId && num(line.quantity) > 0;
}

export function buildPurchaseOrderPayload(params: {
  vendorId: string;
  lines: PurchaseOrderLine[];
  expectedDate?: string;
  notes?: string;
}): PurchaseOrderInput | null {
  const items = params.lines.filter(isComplete).map((line) => {
    const taxValues: Record<string, number> = {};
    const base = lineBase(line);
    if (num(line.icmsPercent) > 0) taxValues['ICMS'] = +(base * (num(line.icmsPercent) / 100)).toFixed(2);
    if (num(line.ipiPercent) > 0) taxValues['IPI'] = +(base * (num(line.ipiPercent) / 100)).toFixed(2);

    return {
      productId: line.productId,
      quantity: num(line.quantity),
      unitValue: num(line.unitValue),
      ...(Object.keys(taxValues).length > 0 ? { taxValues } : {}),
    };
  });

  if (!params.vendorId || items.length === 0) return null;

  return {
    vendorId: params.vendorId,
    items,
    ...(params.expectedDate ? { expectedDate: params.expectedDate } : {}),
    ...(params.notes ? { notes: params.notes } : {}),
  };
}
